import {Alert, Button, Modal} from 'antd';
import React, {useState} from 'react';
import {FILTER_DETAILS_CSV_ID, FILTER_DETAILS_PREFIX} from './FilterDetails';
import {queryBuilderApiUrl} from '../../../actions/api';
import JsFileDownloader from 'js-file-downloader';
import {FileExcelOutlined, FileOutlined, LoadingOutlined} from '@ant-design/icons';
import styles from './ExportDialog.module.scss'
import {CONFIG} from "../../../util/config";
import {QueryResult} from "../../../containers/QueryBuilder/types/Query";

interface ExportDialogProps {
    id: string | undefined
    queryDuration: number
    queryResult: QueryResult | undefined
    isOpen: boolean
    hideExportModal: () => void
}

const ExportDialog = ({id, queryDuration, queryResult, isOpen, hideExportModal}: ExportDialogProps) => {

    const [isDownloading, setDownloading] = useState(false)
    const exportWaitTimeOut = 5 * 60 * 1000 // 300 seconds
    const queryRunTimeThreshold = (exportWaitTimeOut / 1000) / 2 // 150 seconds

    const exportInProgressModal = () => {
        return Modal.info({
            title: <strong>File Export In Progress...</strong>,
            content: (
                <div>
                    <p>Vantage is processing your file. Due to the query's complexity, the export can't
                        be
                        downloaded automatically.</p>
                    <p>You'll receive an email when it's ready. To download it, go to:</p>
                    <p><strong>Saved Queries {">"} Queries {">"} Query Progress List.</strong></p>
                </div>
            ),
        })
    }

    const downloadErrorModal = () => {
        return Modal.error({
            title: <strong>Download Error</strong>,
            content: (
                <div>
                    <p>Vantage could not download the file due to technical reasons.</p>
                    <p>Please contact the Vantage support team.</p>
                </div>
            ),
        })
    }

    const exportErrorModal = () => {
        return Modal.error({
            title: <strong>Export Error</strong>,
            content: (
                <div>
                    <p>Vantage could not export the file due to technical reasons.</p>
                    <p>Please contact the Vantage support team.</p>
                </div>
            ),
        })
    }

    const downloadSuccessModal = () => {
        return Modal.info({
            title: <strong>Download started</strong>,
            content: (
                <div><p>The requested file is being downloaded</p></div>
            ),
        })
    }

    const exportQueryBuilderResultsToFile = async (queryId: string,
                                                   isResultWithinDisplayLimit: boolean,
                                                   filterDetailsText: string,
                                                   exportFormat: string): Promise<void> => {


        const fileExportAPIURL = queryBuilderApiUrl() + `/export/${queryId}/${exportFormat}`
        const fileUploadAPIURL = queryBuilderApiUrl() + `/uploadToS3/${queryId}/${exportFormat}`

        const body = JSON.stringify({filterDetailsText: filterDetailsText})

        const uploadToS3 = () => {
            return fetch(fileUploadAPIURL, {
                method: "POST",
                body: body,
                headers: {"Content-type": "application/json; charset=UTF-8"},
                ...(CONFIG.isDev && { credentials: 'include' })
            }).then((response) => {
                if (response.status === 202) {
                    exportInProgressModal();
                } else {
                    downloadErrorModal();
                }
            }).catch(() => {
                downloadErrorModal();
            }).finally(() => {
                setDownloading(false)
            });
        }

        const exportSlowQuery = () => {
            return fetch(`${fileExportAPIURL}/true`, {
                method: "POST",
                body: body,
                headers: {"Content-type": "application/json; charset=UTF-8"},
                ...(CONFIG.isDev && { credentials: 'include' })
            }).then((response) => {
                if (response.status === 202) {
                    exportInProgressModal();
                } else {
                    exportErrorModal();
                }
            }).catch(() => {
                exportErrorModal();
            }).finally(() => {
                setDownloading(false)
            });
        }

        // for queries that are within the display limit but take a long time to run
        if (queryDuration > queryRunTimeThreshold) {
            hideExportModal()
            setDownloading(false)
            await exportSlowQuery()
        } else {
            if (isResultWithinDisplayLimit) {

                // try to initiate the download when the query is small enough
                new JsFileDownloader({
                    method: "POST",
                    url: `${fileExportAPIURL}/false`,
                    timeout: exportWaitTimeOut,
                    body: body,
                    contentType: "application/json"
                }).then(function (value: any) {
                    if (value.request.status === 200) {
                        hideExportModal()
                        downloadSuccessModal()
                    }
                }).catch(async function (error) {
                    //the download can still fail and error out for various reasons including a timeout
                    console.log(error)
                    hideExportModal()
                    downloadErrorModal()
                }).finally(() => {
                    setDownloading(false)
                })
            } else {
                // directly upload large query files to S3
                hideExportModal()
                await uploadToS3()
            }
        }

    }

    const startExport = (exportFormat: string) => {
        setDownloading(true)
        const filterDetailsElement = document.querySelector(`[data-testid="${FILTER_DETAILS_CSV_ID}"]`)
        // @ts-ignore
        const filterDetailsText = filterDetailsElement.textContent!.replace(FILTER_DETAILS_PREFIX, '')
        exportQueryBuilderResultsToFile(id!, queryResult?.valueAvailable!, filterDetailsText, exportFormat)
    }

    const largeQueryExport = !(queryResult && queryResult.valueAvailable);

    const largeQueryExportMessage = <div>Your query is large and may take some time to process.<br/> <br/>
        You can retrieve the report from the <strong>Query Progress List</strong> once it is ready.<br/><br/></div>

    const largeExportAlert =
        <> <br/><br/>
        <Alert data-testid={'exporting-info'} description="You will be receive an email when the results are ready. You can close this dialog now."
               type="info"
               showIcon/>
        </>

    const exportFormatMessage = <div><strong>Please select your preferred file export format:</strong></div>

    if (!isOpen) return <></>

    return (
        <Modal
            title={'Select Export Type'}
            destroyOnClose={true}
            onCancel={hideExportModal}
            open={isOpen}
            footer={null}
        >

            {largeQueryExport ? largeQueryExportMessage : null}

            {exportFormatMessage}<br/>

            <Button className={styles.excelButton}
                    icon = {<FileExcelOutlined/>}
                    disabled={isDownloading}
                    onClick={() => {startExport('xlsx')}}
            >Styled Excel
            </Button>

            <Button className={styles.csvButton}
                    icon = {<FileOutlined />}
                    disabled={isDownloading}
                    onClick={() => {startExport('csv')}}
            >CSV
            </Button>

            {isDownloading && (largeQueryExport ? largeExportAlert : null)} <br/><br/>

            {isDownloading ? <Alert data-testid='exporting-spinner' message='Exporting...'
                                    type="success"
                                    showIcon
                                    icon={<LoadingOutlined/>}/>
                : null
            }

        </Modal>
    )
}

export default ExportDialog